import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { CalendarClock, CheckCircle2, Star, Users } from 'lucide-react';
import type { SiteConfig } from '../../siteContentTypes.ts';

const CARD_ICONS = [CheckCircle2, Users, CalendarClock, Star];

export function Hero({ config }: { config: SiteConfig }) {
  const { hero } = config;
  const reduceMotion = useReducedMotion();
  const reversed = hero.layout === 'left';
  const centered = hero.layout === 'center';

  return (
    <section style={{ padding: '64px 24px 40px', maxWidth: 1160, margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: centered ? 'column' : reversed ? 'row-reverse' : 'row',
          alignItems: 'center',
          gap: 48,
          textAlign: centered ? 'center' : 'left',
        }}
        className="hero-layout"
      >
        <motion.div
          initial={reduceMotion ? undefined : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20, alignItems: centered ? 'center' : 'flex-start' }}
        >
          {hero.badge && (
            <span className="badge badge-available" style={{ fontSize: 12.5 }}>
              {hero.badge}
            </span>
          )}
          <h1 style={{ fontSize: 'clamp(30px, 5vw, 52px)', fontWeight: 800, lineHeight: 1.15, margin: 0, maxWidth: 620 }}>
            {hero.title}
          </h1>
          <p style={{ fontSize: 17, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0, maxWidth: 540 }}>
            {hero.subtitle}
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: centered ? 'center' : 'flex-start' }}>
            <Link to={hero.ctaPrimaryUrl} className="btn btn-primary" style={{ width: 'auto', padding: '14px 26px', fontSize: 15 }}>
              {hero.ctaPrimaryText}
            </Link>
            <Link to={hero.ctaSecondaryUrl} className="btn btn-secondary" style={{ width: 'auto', padding: '14px 26px', fontSize: 15 }}>
              {hero.ctaSecondaryText}
            </Link>
          </div>
        </motion.div>

        {!centered && (
          <div style={{ flex: 1, position: 'relative', minHeight: 360, width: '100%' }}>
            <div
              style={{
                borderRadius: 24,
                background: 'linear-gradient(135deg, var(--accent-tint), var(--surface))',
                border: '1px solid var(--border)',
                height: 360,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {hero.image ? (
                <img src={hero.image} alt="" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 20 }} />
              ) : (
                <div style={{ color: 'var(--text-tertiary)', fontSize: 13, fontWeight: 700 }}>Aperçu de l'application</div>
              )}
            </div>

            {hero.floatingCards.slice(0, 4).map((card, i) => {
              const Icon = CARD_ICONS[i % CARD_ICONS.length];
              const positions = [
                { top: -10, left: -10 },
                { top: 30, right: -20 },
                { bottom: 40, left: -30 },
                { bottom: -10, right: 10 },
              ];
              return (
                <div
                  key={card.text}
                  className="card landing-floating-card"
                  style={{
                    position: 'absolute',
                    ...positions[i % positions.length],
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 14px',
                    fontSize: 12.5,
                    fontWeight: 700,
                    animationDelay: `${i * 0.6}s`,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  }}
                >
                  <Icon size={15} color="var(--accent-dark)" />
                  {card.text}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
